/**
 * Animated Product Hover Effects
 * This script enhances the animated product grid with advanced GSAP animations
 * and integrates with Barba.js for page transitions
 * 
 * Uses standardized navigator.storage API instead of deprecated StorageType.persistent
 */

// Utility function to use modern storage API instead of deprecated StorageType.persistent
// This helps prevent deprecation warnings in modern browsers
function requestPersistentStorage() {
  if (navigator.storage && navigator.storage.persist) {
    navigator.storage.persist().then(isPersisted => {
      if (isPersisted) {
        console.debug('Storage persistence granted.');
      } else {
        console.debug('Storage persistence not granted.');
      }
    });
  }
}

class AnimatedProductHover {
  constructor() {
    // Check if required libraries are loaded
    if (typeof gsap === 'undefined') {
      console.warn('GSAP is not loaded. Advanced animations will not work.');
      return;
    }
    
    // Use modern storage API if available
    requestPersistentStorage();
    
    this.initializeElements();
    this.setupEventListeners();
    this.setupBarbaIntegration();
  }

  initializeElements() {
    this.productBlocks = document.querySelectorAll('.animated-product-block');
    this.productGrids = document.querySelectorAll('[data-animated-product-grid]');
  }

  setupEventListeners() {
    // Apply hover animations to all product blocks
    this.productBlocks.forEach(block => {
      const primaryImage = block.querySelector('.animated-product-block__primary-image');
      const solidBg = block.querySelector('.animated-product-block__solid-bg');
      const imageBg = block.querySelector('.animated-product-block__image-bg');
      
      if (primaryImage && solidBg) {
        // Create hover animation timeline
        const hoverTimeline = gsap.timeline({ paused: true });
        
        // Animar o background sólido para desaparecer
        hoverTimeline.to(solidBg, {
          opacity: 0,
          duration: 0.4,
          ease: 'power2.inOut'
        });
        
        // Animar o background de imagem para aparecer
        if (imageBg) {
          hoverTimeline.to(imageBg, {
            opacity: 1,
            duration: 0.4,
            ease: 'power2.inOut'
          }, '-=0.4');
        }
        
        // Animar a imagem principal com um efeito sutil
        hoverTimeline.to(primaryImage, {
          scale: 1.05,
          duration: 0.5,
          ease: 'power2.out'
        }, '-=0.3');
        
        // Add event listeners to play/reverse the timeline
        block.addEventListener('mouseenter', () => {
          hoverTimeline.play();
        });
        
        block.addEventListener('mouseleave', () => {
          hoverTimeline.reverse();
        });
      }
    });
  }

  setupBarbaIntegration() {
    // Only setup if Barba.js is available
    if (typeof barba === 'undefined') {
      return;
    }
    
    // Add hooks for Barba.js page transitions
    barba.hooks.after((data) => {
      // Re-initialize elements after page transition
      this.initializeElements();
      
      // Animate product grid items with staggered entrance
      this.productGrids.forEach(grid => {
        const gridItems = grid.querySelectorAll('.animated-product-block');
        
        gsap.set(gridItems, { opacity: 0, y: 30 });
        
        gsap.to(gridItems, {
          opacity: 1,
          y: 0,
          duration: 0.6,
          stagger: 0.1,
          ease: 'power3.out',
          clearProps: 'all'
        });
      });
    });
  }

  // Method to manually trigger animations (useful for theme editor)
  refreshAnimations() {
    this.initializeElements();
    this.setupEventListeners();
  }
}

// Initialize when DOM is loaded
document.addEventListener('DOMContentLoaded', () => {
  window.animatedProductHover = new AnimatedProductHover();
});

// Re-initialize when Shopify section is reloaded in theme editor
document.addEventListener('shopify:section:load', (event) => {
  if (event.detail.sectionId && event.detail.sectionId.includes('animated-product-grid')) {
    window.animatedProductHover.refreshAnimations();
  }
});

// Re-initialize after page transitions
document.addEventListener('page:loaded', () => {
  if (window.animatedProductHover) {
    window.animatedProductHover.refreshAnimations();
  } else {
    window.animatedProductHover = new AnimatedProductHover();
  }
});
